"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

export type NotificationCategory = "orders" | "schedules" | "messages" | "news" | "products" | "services" | "users" | "other";

export interface AdminNotification {
  id: string;
  title: string;
  message: string;
  createdAt: number; // epoch ms
  unread: boolean;
  category: NotificationCategory;
  href?: string; // optional deep link
}

interface NotificationsContextValue {
  notifications: AdminNotification[];
  unreadCount: number;
  addNotification: (n: Omit<AdminNotification, "id" | "createdAt" | "unread"> & Partial<Pick<AdminNotification, "href">>) => AdminNotification;
  markAllAsRead: () => void;
  markAsRead: (id: string) => void;
  clear: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

const STORAGE_KEY = "sdtc.admin.notifications.v1";

function loadFromStorage(): AdminNotification[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AdminNotification[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveToStorage(list: AdminNotification[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // ignore quota/storage errors silently
  }
}

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<AdminNotification[]>(() => loadFromStorage());
  const hasHydratedRef = useRef(false);

  useEffect(() => {
    // one-time hydration sync for SSR/CSR boundary
    if (!hasHydratedRef.current) {
      setNotifications(loadFromStorage());
      hasHydratedRef.current = true;
    }
  }, []);

  useEffect(() => {
    saveToStorage(notifications);
  }, [notifications]);

  const addNotification: NotificationsContextValue["addNotification"] = useCallback((n) => {
    const newItem: AdminNotification = {
      id: crypto.randomUUID(),
      title: n.title,
      message: n.message,
      category: n.category,
      href: n.href,
      createdAt: Date.now(),
      unread: true,
    };
    setNotifications((prev) => [newItem, ...prev].slice(0, 100));
    return newItem;
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((x) => ({ ...x, unread: false })));
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) => prev.map((x) => (x.id === id ? { ...x, unread: false } : x)));
  }, []);

  const clear = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = useMemo(() => notifications.filter((n) => n.unread).length, [notifications]);

  const value: NotificationsContextValue = useMemo(
    () => ({ notifications, unreadCount, addNotification, markAllAsRead, markAsRead, clear }),
    [notifications, unreadCount, addNotification, markAllAsRead, markAsRead, clear]
  );

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationsProvider");
  return ctx;
}

// Lightweight helper to dispatch notifications from anywhere without a hook (e.g., async handlers)
export function dispatchAdminNotification(detail: Omit<AdminNotification, "id" | "createdAt" | "unread">) {
  if (typeof window === "undefined") return;
  const event = new CustomEvent("sdtc-admin-notification", { detail });
  window.dispatchEvent(event);
}

// Bridge: when provider mounts, listen for global dispatches and add them
export function NotificationsBridge() {
  const { addNotification } = useNotifications();
  useEffect(() => {
    function handler(e: Event) {
      const custom = e as CustomEvent<Omit<AdminNotification, "id" | "createdAt" | "unread">>;
      const d = custom.detail;
      if (!d) return;
      addNotification(d);
    }
    window.addEventListener("sdtc-admin-notification", handler as EventListener);
    return () => window.removeEventListener("sdtc-admin-notification", handler as EventListener);
  }, [addNotification]);
  return null;
}








